import { PAPRIKA, SEA, STROKE, VELVET, draw, mix, r2, scaleAt, track, tx } from "./motion";

// The four "What I do" icons, ported from the Figr prototype (figr/icons/*.tsx).
// Each render(t) draws the icon at progress t (0–1); t = 1 is the static icon.

/* ---------- Frontend: brackets part, slash draws in, brackets snap shut ---------- */
const SLASH_LEN = 23.09;
const frontend = {
  duration: 1000,
  render: (t) => {
    const x = track([[0, 6], [0.4, -4], [0.75, -4, "linear"], [1, 0, "pop"]], t);
    const slash = track([[0, 0], [0.4, 0, "linear"], [0.75, 1]], t);
    return (
      <>
        <polyline points="25,23 18,32 25,41" {...STROKE} transform={tx(x)} />
        <line x1="35.5" y1="21" x2="28.5" y2="43" {...STROKE} {...draw(SLASH_LEN, slash)} />
        <polyline points="39,23 46,32 39,41" {...STROKE} transform={tx(-x)} />
      </>
    );
  },
};

/* ---------- Machine learning: network wires up, layers light, output pops ---------- */
const N = {
  "1-1": [19, 21.5],
  "1-2": [19, 42.5],
  "2-1": [31.5, 21.5],
  "2-2": [31.5, 42.5],
  "3-1": [43.5, 32],
};
const LINES = [
  ["1-1", "2-1"], ["1-1", "2-2"], ["1-2", "2-1"], ["1-2", "2-2"],
  ["2-1", "3-1"], ["2-2", "3-1"],
];
const lineLength = (a, b) => Math.hypot(N[b][0] - N[a][0], N[b][1] - N[a][1]);
const LIGHT = {
  1: { fill: [[0.4, 0], [0.55, 1]], scale: [[0.4, 1], [0.5, 1.25], [0.62, 1]] },
  2: { fill: [[0.55, 0], [0.7, 1]], scale: [[0.55, 1], [0.65, 1.25], [0.75, 1]] },
};
const ml = {
  duration: 1100,
  render: (t) => {
    const d1 = track([[0, 0], [0.25, 1]], t);
    const d2 = track([[0.15, 0], [0.4, 1]], t);
    const outFill = track([[0.75, 0], [0.86, 1]], t);
    const outScale = track([[0.75, 0.7], [0.9, 1.3], [1, 1]], t);
    const core = track([[0.85, 0], [1, 1]], t);
    const [ox, oy] = N["3-1"];
    return (
      <>
        {LINES.map(([a, b]) => (
          <line
            key={a + b}
            x1={N[a][0]} y1={N[a][1]} x2={N[b][0]} y2={N[b][1]}
            {...STROKE}
            {...draw(lineLength(a, b), a.startsWith("1") ? d1 : d2)}
          />
        ))}
        {["1-1", "1-2", "2-1", "2-2"].map((id) => {
          const light = LIGHT[id[0]];
          const [cx, cy] = N[id];
          return (
            <circle
              key={id}
              cx={cx} cy={cy} r={3.5}
              stroke={SEA} strokeWidth={2.5}
              fill={mix(PAPRIKA, SEA, track(light.fill, t))}
              transform={scaleAt(cx, cy, track(light.scale, t))}
            />
          );
        })}
        <circle cx={ox} cy={oy} r={4.25} stroke={SEA} strokeWidth={2.5} fill={mix(PAPRIKA, SEA, outFill)} transform={scaleAt(ox, oy, outScale)} />
        <circle cx={ox} cy={oy} r={1.6} fill={VELVET} opacity={core >= 0.999 ? undefined : r2(core)} transform={scaleAt(ox, oy, outScale)} />
      </>
    );
  },
};

/* ---------- Full stack: bolt zaps client → server, both dots pulse ---------- */
const BOLT_LEN = 42.28;
const CLIENT = [19, 32];
const SERVER = [45, 32];
const fullstack = {
  duration: 900,
  render: (t) => {
    const boltDraw = track([[0, 0], [0.4, 1]], t);
    const boltX = track([[0, -7], [0.4, 3], [0.75, 0]], t);
    const client = track([[0, 1], [0.15, 1.4], [0.4, 1], [0.75, 1, "linear"], [0.87, 1.18], [1, 1]], t);
    const server = track([[0.4, 1], [0.55, 1.4], [0.75, 1], [0.87, 1.18], [1, 1]], t);
    const clientRing = track([[0, 3.25], [0.4, 9]], t);
    const clientRingOpacity = track([[0, 0.9], [0.4, 0]], t);
    const serverRing = track([[0.4, 3.25], [0.75, 9]], t);
    const serverRingOpacity = t < 0.4 ? 0 : track([[0.4, 0.9], [0.75, 0]], t);
    return (
      <>
        <circle cx={CLIENT[0]} cy={CLIENT[1]} r={r2(clientRing)} {...STROKE} opacity={r2(clientRingOpacity)} />
        <circle cx={SERVER[0]} cy={SERVER[1]} r={r2(serverRing)} {...STROKE} opacity={r2(serverRingOpacity)} />
        <circle cx={CLIENT[0]} cy={CLIENT[1]} r={3.5} fill={SEA} transform={scaleAt(CLIENT[0], CLIENT[1], client)} />
        <polyline points="34,20 26.5,33.5 37.5,30.5 30,44" {...STROKE} transform={tx(boltX)} {...draw(BOLT_LEN, boltDraw)} />
        <circle cx={SERVER[0]} cy={SERVER[1]} r={3.5} fill={SEA} transform={scaleAt(SERVER[0], SERVER[1], server)} />
      </>
    );
  },
};

/* ---------- Interfaces: pen nib dips and draws one curve ---------- */
const P = [[16.5, 41], [19.5, 33.5], [25, 45], [29.5, 36.5]];
const CURVE_D = "M16.5 41 C19.5 33.5 25 45 29.5 36.5";
const bezier = (s) => {
  const m = 1 - s;
  const a = m * m * m;
  const b = 3 * m * m * s;
  const c = 3 * m * s * s;
  const d = s * s * s;
  return [a * P[0][0] + b * P[1][0] + c * P[2][0] + d * P[3][0], a * P[0][1] + b * P[1][1] + c * P[2][1] + d * P[3][1]];
};
// The nib tip follows the curve by arc length, so it moves at an even speed.
const SAMPLES = 200;
const points = [];
const distance = [0];
for (let i = 0; i <= SAMPLES; i++) {
  points.push(bezier(i / SAMPLES));
  if (i > 0) distance.push(distance[i - 1] + Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]));
}
const CURVE_LEN = distance[SAMPLES];
const pointAt = (f) => {
  const target = Math.max(0, Math.min(1, f)) * CURVE_LEN;
  let i = 1;
  while (i < SAMPLES && distance[i] < target) i++;
  const segment = distance[i] - distance[i - 1] || 1;
  const u = (target - distance[i - 1]) / segment;
  return [points[i - 1][0] + (points[i][0] - points[i - 1][0]) * u, points[i - 1][1] + (points[i][1] - points[i - 1][1]) * u];
};
const interfaces = {
  duration: 1100,
  render: (t) => {
    const p = track([[0.4, 0], [0.75, 1]], t);
    const dipX = track([[0, 4], [0.4, 0]], t);
    const dipY = track([[0, -10], [0.4, 0]], t);
    const lift = track([[0.75, 0], [0.87, -1.5], [1, 0]], t);
    const rotation = track([[0, 30], [0.4, 45], [0.6, 40], [0.75, 49], [1, 45, "pop"]], t);
    const [x, y] = pointAt(p);
    return (
      <>
        <path d={CURVE_D} {...STROKE} {...draw(CURVE_LEN, p)} />
        <g transform={`translate(${r2(x + dipX)} ${r2(y + dipY + lift)}) rotate(${r2(rotation)})`}>
          <path d="M0 0 L-5.5 -9.5 L-3.5 -14 L3.5 -14 L5.5 -9.5 Z" {...STROKE} fill={PAPRIKA} />
          <line x1="0" y1="-2.75" x2="0" y2="-6.5" {...STROKE} />
          <circle cx="0" cy="-9.4" r="1.6" fill={SEA} />
          <line x1="-3.5" y1="-17.5" x2="3.5" y2="-17.5" {...STROKE} />
        </g>
      </>
    );
  },
};

export const SERVICE_ICONS = { frontend, ml, fullstack, interfaces };

export function ServiceIcon({ icon, t, size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false" className="service-icon">
      <circle cx="32" cy="32" r="32" fill={PAPRIKA} />
      {icon.render(t)}
    </svg>
  );
}
